import { addSeconds, differenceInSeconds } from "date-fns";
import { useEffect, useMemo, useRef } from "react";

import { WebSocketComponent } from "components";
import { useWebSocketStatus } from "components/WebSocket/hooks/useWebSocketStatus";
import { typedQueryClient, usePermanentRealTimeUser } from "hooks";
import { setOtherUsersOnTheSameAppointmentDetailsPage, useOtherUsersOnTheSameAppointmentDetailsPage } from "modules/AppointmentDetails/hooks";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";

import type { WebSocketMessageListener } from "components";
import type { User } from "models";

export type LiveUser = {
  first_name: string;
  last_name: string;
  id: number;
  profile_picture: string;
  timeOut: Date;
};

const TIMEOUT = 1;

export const useLiveUsers = (appointmentId: number) => {
  const intervalRef = useRef<number | null>(null);
  const { isConnected } = useWebSocketStatus();
  const { user } = usePermanentRealTimeUser();
  const { data: users } = useOtherUsersOnTheSameAppointmentDetailsPage();

  const listeners = useMemo(
    (): WebSocketMessageListener<User>[] => [
      {
        model: "_ActiveUsersOnAppointmentDetailsPage",
        action: "ping",
        id: appointmentId,
        callback: ({ data: userInfo }) => {
          if (userInfo.id === user?.id) return;

          const otherUsersOnTheSameAppointment = typedQueryClient.getQueryData(AppointmentDetailsKeys.otherUsersOnTheSameAppointmentDetailsPage) || [];
          const isUserPresent = otherUsersOnTheSameAppointment.some(u => u.id === userInfo.id);

          let updatedUsers: LiveUser[] = otherUsersOnTheSameAppointment.map(prevUser =>
            prevUser.id === userInfo.id ? { ...prevUser, timeOut: addSeconds(new Date(), TIMEOUT) } : prevUser
          );

          if (!isUserPresent) {
            const { first_name, last_name, id, profile_picture } = userInfo;
            if (!id) return;

            updatedUsers = [
              ...otherUsersOnTheSameAppointment,
              { first_name, last_name, id, profile_picture: profile_picture || "", timeOut: addSeconds(new Date(), TIMEOUT) }
            ];
          }

          setOtherUsersOnTheSameAppointmentDetailsPage(updatedUsers);
        }
      }
    ],
    [appointmentId, user?.id]
  );

  useEffect(() => {
    if (!user || !isConnected) return;

    const listenersSubscribers = listeners.map(listener => WebSocketComponent.addMessageListener(listener));
    WebSocketComponent.subscribeToAppointmentQueue(appointmentId);

    return () => {
      listenersSubscribers.forEach(ls => ls());
      WebSocketComponent.unsubscribeFromAppointmentQueue(appointmentId);
    };
  }, [isConnected, user, appointmentId, listeners]);

  useEffect(() => {
    const clearOutInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    if (!user || !isConnected) {
      clearOutInterval();
      return;
    }

    WebSocketComponent.sendAppointmentActiveUserPing(appointmentId, user);

    intervalRef.current = window.setInterval(() => {
      const otherUsersOnTheSameAppointment = typedQueryClient.getQueryData(AppointmentDetailsKeys.otherUsersOnTheSameAppointmentDetailsPage) || [];
      const updatedUsers = otherUsersOnTheSameAppointment.filter(u => differenceInSeconds(new Date(), u.timeOut) <= TIMEOUT);
      setOtherUsersOnTheSameAppointmentDetailsPage(updatedUsers);

      if (updatedUsers.length > 0) WebSocketComponent.sendAppointmentActiveUserPing(appointmentId, user);
      else clearOutInterval();
    }, 1000);

    return () => {
      clearOutInterval();
    };
  }, [users.length, isConnected, user?.id, appointmentId]);

  return { users };
};
